import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, lazy, Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

// Lazy-load page components to optimize initial bundle size and enable dynamic icon loading
const Index = lazy(() => import("./pages/Index"));
const PoolBuilding = lazy(() => import("./pages/PoolBuilding"));
const WeeklyPoolService = lazy(() => import("./pages/WeeklyMaintenance"));
const PoolService = lazy(() => import("./pages/PoolService"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Contact = lazy(() => import("./pages/Contact"));
const PoolDesignGuide = lazy(() => import("./pages/PoolDesignGuide"));
const ServiceArea = lazy(() => import("./pages/ServiceArea"));
const CocktailPoolPage = lazy(() => import("./pages/CocktailPoolPage"));
const OutdoorLiving = lazy(() => import("./pages/OutdoorLiving"));
const PoolRemodeling = lazy(() => import("./pages/PoolRemodeling"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const CookiePreferences = lazy(() => import("./pages/CookiePreferences"));
const ConsentAuditLog = lazy(() => import("./pages/ConsentAuditLog"));
const ServiceContinuityPolicy = lazy(
  () => import("./pages/ServiceContinuityPolicy"),
);
const MaintenanceAgreement = lazy(() => import("./pages/MaintenanceAgreement"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PoolBuilderMontgomeryCounty = lazy(
  () => import("./pages/PoolBuilderMontgomeryCounty"),
);
const PoolBuilderConroe = lazy(() => import("./pages/PoolBuilderConroe"));
const PoolBuilderTheWoodlands = lazy(
  () => import("./pages/PoolBuilderTheWoodlands"),
);
const PoolBuilderMagnolia = lazy(() => import("./pages/PoolBuilderMagnolia"));
const PoolBuilderSpring = lazy(() => import("./pages/PoolBuilderSpring"));
const PoolBuilderTomball = lazy(() => import("./pages/PoolBuilderTomball"));
const PoolBuilderWillis = lazy(() => import("./pages/PoolBuilderWillis"));
const LuxuryPoolCostGuide = lazy(() => import("./pages/LuxuryPoolCostGuide"));
const ReviewsPage = lazy(() => import("./pages/Reviews"));
const ServiceAreasIndex = lazy(() => import("./pages/ServiceAreasIndex"));
const FAQPage = lazy(() => import("./pages/FAQ"));
const FinancingPage = lazy(() => import("./pages/Financing"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const Gallery = lazy(() => import("./pages/Gallery"));
const ProjectGallery = lazy(() => import("./pages/ProjectGallery"));
const PoolBuildingAds = lazy(() => import("./pages/PoolBuildingAds"));
const PoolServiceAds = lazy(() => import("./pages/PoolServiceAds"));
const PoolMaintenanceAds = lazy(() => import("./pages/PoolMaintenanceAds"));
const MaintenancePlans = lazy(() => import("./pages/MaintenancePlans"));

import { ScrollHandler } from "./components/ScrollHandler";
import { BackToTop } from "./components/BackToTop";

const CanonicalUpdater = () => {
  const location = useLocation();

  useEffect(() => {
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (!canonicalTag) {
      canonicalTag = document.createElement("link");
      canonicalTag.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalTag);
    }
    const baseUrl = "https://solsticepools.com";
    const currentPath = location.pathname === "/" ? "" : location.pathname;
    canonicalTag.setAttribute("href", `${baseUrl}${currentPath}`);
  }, [location.pathname]);

  return null;
};

const queryClient = new QueryClient();

const PageWrapper = ({ children }: { children: React.ReactNode }) => {
  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
    >
      {children}
    </motion.div>
  );
};

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes
        location={location}
        key={location.pathname + (location.state?.forceReload || "")}
      >
        <Route
          path="/"
          element={
            <PageWrapper>
              <Index />
            </PageWrapper>
          }
        />
        <Route
          path="/pool-building"
          element={
            <PageWrapper>
              <PoolBuilding />
            </PageWrapper>
          }
        />
        <Route
          path="/pool-maintenance"
          element={
            <PageWrapper>
              <WeeklyPoolService />
            </PageWrapper>
          }
        />
        <Route
          path="/pool-service"
          element={
            <PageWrapper>
              <PoolService />
            </PageWrapper>
          }
        />
        <Route
          path="/blog"
          element={
            <PageWrapper>
              <Blog />
            </PageWrapper>
          }
        />
        <Route
          path="/blog/:slug"
          element={
            <PageWrapper>
              <BlogPost />
            </PageWrapper>
          }
        />
        <Route
          path="/contact"
          element={
            <PageWrapper>
              <Contact />
            </PageWrapper>
          }
        />
        <Route
          path="/guide"
          element={
            <PageWrapper>
              <PoolDesignGuide />
            </PageWrapper>
          }
        />
        <Route
          path="/areas/:slug"
          element={
            <PageWrapper>
              <ServiceArea />
            </PageWrapper>
          }
        />
        <Route
          path="/areas/chaple-hill-tx"
          element={<Navigate to="/areas/chappell-hill-tx" replace />}
        />
        <Route
          path="/areas/fulsher-tx"
          element={<Navigate to="/areas/fulshear-tx" replace />}
        />
        <Route
          path="/areas/belville-tx"
          element={<Navigate to="/areas/bellville-tx" replace />}
        />
        <Route
          path="/cocktail-pools"
          element={
            <PageWrapper>
              <CocktailPoolPage />
            </PageWrapper>
          }
        />
        <Route
          path="/spools-cocktail-pools"
          element={<Navigate to="/cocktail-pools" replace />}
        />
        <Route
          path="/custom-pools"
          element={<Navigate to="/pool-building" replace />}
        />
        <Route
          path="/outdoor-living"
          element={<Navigate to="/pool-building" replace />}
        />
        <Route
          path="/pool-remodeling"
          element={<Navigate to="/pool-building" replace />}
        />
        <Route
          path="/pool-repair"
          element={<Navigate to="/pool-service" replace />}
        />
        <Route
          path="/weekly-maintenance"
          element={<Navigate to="/pool-maintenance" replace />}
        />
        <Route
          path="/privacy-policy"
          element={
            <PageWrapper>
              <PrivacyPolicy />
            </PageWrapper>
          }
        />
        <Route
          path="/terms-and-conditions"
          element={
            <PageWrapper>
              <TermsAndConditions />
            </PageWrapper>
          }
        />
        <Route
          path="/cookie-preferences"
          element={
            <PageWrapper>
              <CookiePreferences />
            </PageWrapper>
          }
        />
        <Route
          path="/admin/consent-log"
          element={
            <PageWrapper>
              <ConsentAuditLog />
            </PageWrapper>
          }
        />
        <Route
          path="/service-continuity-and-billing-policy"
          element={
            <PageWrapper>
              <ServiceContinuityPolicy />
            </PageWrapper>
          }
        />
        <Route
          path="/maintenance-service-agreement"
          element={
            <PageWrapper>
              <MaintenanceAgreement />
            </PageWrapper>
          }
        />
        <Route
          path="/pool-builder-montgomery-county-tx"
          element={
            <PageWrapper>
              <PoolBuilderMontgomeryCounty />
            </PageWrapper>
          }
        />
        <Route
          path="/pool-builder-conroe-tx"
          element={
            <PageWrapper>
              <PoolBuilderConroe />
            </PageWrapper>
          }
        />
        <Route
          path="/pool-builder-the-woodlands-tx"
          element={
            <PageWrapper>
              <PoolBuilderTheWoodlands />
            </PageWrapper>
          }
        />
        <Route
          path="/pool-builder-magnolia-tx"
          element={
            <PageWrapper>
              <PoolBuilderMagnolia />
            </PageWrapper>
          }
        />
        <Route
          path="/pool-builder-spring-tx"
          element={
            <PageWrapper>
              <PoolBuilderSpring />
            </PageWrapper>
          }
        />
        <Route
          path="/pool-builder-tomball-tx"
          element={
            <PageWrapper>
              <PoolBuilderTomball />
            </PageWrapper>
          }
        />
        <Route
          path="/pool-builder-willis-tx"
          element={
            <PageWrapper>
              <PoolBuilderWillis />
            </PageWrapper>
          }
        />
        <Route
          path="/luxury-pool-cost-guide"
          element={
            <PageWrapper>
              <LuxuryPoolCostGuide />
            </PageWrapper>
          }
        />
        <Route
          path="/reviews"
          element={
            <PageWrapper>
              <ReviewsPage />
            </PageWrapper>
          }
        />
        <Route
          path="/service-areas"
          element={
            <PageWrapper>
              <ServiceAreasIndex />
            </PageWrapper>
          }
        />
        <Route
          path="/faq"
          element={
            <PageWrapper>
              <FAQPage />
            </PageWrapper>
          }
        />
        <Route
          path="/financing"
          element={
            <PageWrapper>
              <FinancingPage />
            </PageWrapper>
          }
        />
        <Route
          path="/thank-you"
          element={
            <PageWrapper>
              <ThankYou />
            </PageWrapper>
          }
        />
        <Route
          path="/gallery"
          element={
            <PageWrapper>
              <Gallery />
            </PageWrapper>
          }
        />
        <Route
          path="/gallery/:slug"
          element={
            <PageWrapper>
              <ProjectGallery />
            </PageWrapper>
          }
        />
        <Route
          path="/pool-building-quote"
          element={
            <PageWrapper>
              <PoolBuildingAds />
            </PageWrapper>
          }
        />
        <Route
          path="/pool-service-quote"
          element={
            <PageWrapper>
              <PoolServiceAds />
            </PageWrapper>
          }
        />
        <Route
          path="/pool-maintenance-quote"
          element={
            <PageWrapper>
              <PoolMaintenanceAds />
            </PageWrapper>
          }
        />
        <Route
          path="/maintenance-plans"
          element={
            <PageWrapper>
              <MaintenancePlans />
            </PageWrapper>
          }
        />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route
          path="*"
          element={
            <PageWrapper>
              <NotFound />
            </PageWrapper>
          }
        />
      </Routes>
    </AnimatePresence>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BackToTop />

      {/* Preload hero background videos for instant playback on page transitions */}
      <div
        style={{
          position: "absolute",
          width: "1px",
          height: "1px",
          padding: "0",
          margin: "-1px",
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          border: "0",
          pointerEvents: "none",
          opacity: 0,
        }}
        aria-hidden="true"
      >
        <video
          src="https://assets.cdn.filesafe.space/dlzAnHP05FrGG12Cw25m/media/6a4d8511947fe38b7443c365.mp4"
          preload="auto"
          muted
          playsInline
        />
        <video
          src="https://assets.cdn.filesafe.space/dlzAnHP05FrGG12Cw25m/media/6a4d8511b7dbcd4149ee1142.mp4"
          preload="auto"
          muted
          playsInline
        />
        <img
          src="https://assets.cdn.filesafe.space/dlzAnHP05FrGG12Cw25m/media/6a4d8f532467f0ff08a48d7d.jpg"
          alt=""
        />
        <img
          src="https://assets.cdn.filesafe.space/dlzAnHP05FrGG12Cw25m/media/6a4d8f532d9cf80515f95aea.jpg"
          alt=""
        />
      </div>

      <BrowserRouter>
        <CanonicalUpdater />
        <ScrollHandler />
        <Suspense
          fallback={
            <div className="flex items-center justify-center min-h-screen bg-slate-950 text-white">
              <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
          }
        >
          <AnimatedRoutes />
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
